import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  createRef,
} from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import * as debounce from 'lodash.debounce';
import QuickSearchResult from './QuickSearchResult';
import { translate } from '../Services/translation';
import {
  query,
  setSearchQuery,
  toggleShowFullForm,
  handleKeyDown,
  handleClickSearch,
} from '../Actions/QuickSearch.action';
import usePrevious from '@react-hook/previous';

// debouncing function to 200ms so we don't send query request on every key stroke
const debouncedQuery = debounce((dispatch, text) => dispatch(query(text)), 200);

const QuickSearch = () => {
  const {
    query,
    result,
    products,
    pages,
    showResult,
    showFullForm,
    selectedItem,
  } = useSelector((state) => state.quickSearch);
  const [lastClickedNode, setLastClickedNode] = useState(null);
  const searchUrl =
    window.__litium.quickSearchUrl + (query.length > 0 ? `?q=${query}` : '');
  const searchResultRef = useRef();
  const searchContainer = useRef(null);
  const searchInput = useRef(null);
  const dispatch = useDispatch();
  const previousSelectedItem = usePrevious(selectedItem);

  const clickHandler = useCallback((event) => {
    setLastClickedNode(event.target);
  }, []);

  useEffect(() => {
    if (
      searchContainer.current &&
      !searchContainer.current.contains(lastClickedNode) &&
      !(
        searchResultRef.current &&
        searchResultRef.current.contains(lastClickedNode)
      )
    ) {
      showFullForm && dispatch(toggleShowFullForm());
    }
  }, [lastClickedNode]);

  useEffect(() => {
    // listen for click event to hide the search when clicking outside
    window.addEventListener('mousedown', clickHandler);
    return () => window.removeEventListener('mousedown', clickHandler);
  }, [clickHandler]);

  useEffect(() => {
    // set query value if it is avaialble in the Url
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('q')) {
      const query = urlParams.get('q');
      dispatch(setSearchQuery(query));
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedItem !== previousSelectedItem) {
      const el = document.querySelector('.quick-search-result__item--selected');
      el &&
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
    }
  }, [selectedItem, previousSelectedItem]);

  return (
    <div className="quick-search" role="search" ref={searchContainer}>
      <a
        className="header-link"
        onClick={(e) => {
          dispatch(toggleShowFullForm());
          setTimeout(() => {
            searchInput.current && searchInput.current.focus();
          }, 0);
        }}
      >
        <i className="fas fa-search"></i>
      </a>
      {ReactDOM.createPortal(
        <div
          className={`quick-search__form ${
            showFullForm ? 'quick-search__form--force-show' : ''
          }`}
          role="search"
          ref={searchResultRef}
        >
          <div className="quick-search__input--wrapper row">
            <div className="columns small-12">
              <i
                className="quick-search__icon fa fa-search"
                onClick={(e) => dispatch(toggleShowFullForm())}
              ></i>
              <input
                className="quick-search__input"
                type="search"
                placeholder={translate('general.search')}
                autoComplete="off"
                value={decodeURIComponent(query)}
                onChange={(event) => {
                  const text = encodeURIComponent(event.target.value);
                  dispatch(setSearchQuery(text));
                  text && debouncedQuery(dispatch, text);
                }}
                onKeyDown={(event) =>
                  dispatch(handleKeyDown(event, { searchUrl }))
                }
                ref={searchInput}
              />
            </div>
          </div>
          {showResult && (
            <QuickSearchResult
              pages={pages}
              products={products}
              result={result}
              selectedItem={selectedItem}
              searchUrl={searchUrl}
            />
          )}
        </div>,
        document.getElementById('searchResultForm')
      )}
    </div>
  );
};

export default QuickSearch;
